// Class to wrap a table of entries where we can access one by clicking it
import {UI} from "../../stem-core/ui/UIBase";
import {DashboardTitle} from "./DashboardTitle";
import {Router} from "../../stem-core/ui/Router";
import {SimpleTable, MakeLinkingRowClass} from "../ui/SimpleTable";
import {styleRule, StyleSheet} from "../../stem-core/ui/Style";
import {registerStyle} from "../../stem-core/ui/style/Theme";
import {openWindowWithOpener} from "../../blinkpay/UtilsLib.js";
import {getEditingOrLiveSettingsVersion} from "../../client/state/merchant/MerchantSDKSettingsStore.js";
import type {Merchant} from "../../client/state/MerchantStore";
import type {UIChild} from "../../stem-core/ui/UIBase";
import type {StoreClass, StoreObject} from "../../stem-core/state/Store";
import type {StoreId} from "../../stem-core/state/State";
import type {ColumnLike} from "../../stem-core/base/ColumnHandler";

export class DashboardSelectorStyle extends StyleSheet {
    @styleRule
    headerButtons = {
        paddingBottom: 12,
    }
}

// What the URL needs from an entry, plus the version an alias is looked up within
export type SelectableEntry = {id?: StoreId; alias?: string; settingsVersion?: unknown};

// Entry is what the page selects: the store it reads, the table it lists and the URL it builds all use it.
// It defaults to any for pages that keep something other than a store object selected, like a create sentinel.
interface DashboardSelectorPageOptions<Entry extends StoreObject & SelectableEntry> {
    baseURL?: string;
    description?: UIChild;
    merchant?: Merchant;
    store?: StoreClass<Entry>;
    useAliasInURL?: boolean;
    // The URL parts after baseURL, handed over by the Router
    args?: string[];
}

@registerStyle(DashboardSelectorStyle)
export class DashboardSelectorPage<Entry extends StoreObject & SelectableEntry = any, ExtraOptions = {}> extends UI.Element<DashboardSelectorPageOptions<Entry> & ExtraOptions> {
    // The entry the URL selects, or null while the list is shown
    declare selectedEntry?: Entry;

    // Breadcrumb style titles
    renderTitle(subtitle: UIChild = null, description = this.options.description) {
        let title = this.options.title || "NoTitle";
        if (subtitle) {
            title = [
                <a onClick={(event) => {
                        if (event.metaKey || event.ctrlKey) {
                            return;
                        }
                        this.goToEntry(null);
                        event.preventDefault();
                    }}
                    href={this.getUrlForEntry()}
                    style={{cursor: "pointer"}}>{title}</a>,
                " / ",
                subtitle
            ];
        }
        return <DashboardTitle title={title} description={description} />
    }

    // Return an array of url parts
    getUrlForEntry(entry?: Entry | null): string {
        const {baseURL, useAliasInURL} = this.options;
        if (!baseURL) {
            return null;
        }
        const url = [this.options.baseURL];
        // Try to use the alias, but fall back to id just in case
        const entryKey = (useAliasInURL && entry?.alias) || entry?.id;
        if (entryKey) {
            url.push(String(entryKey));
        }
        return Router.formatURL(url);
    }

    goToEntry(entry: Entry | null) {
        this.selectedEntry = entry;
        const url = this.getUrlForEntry(entry);
        if (url) {
            // This will also call a redraw
            Router.changeURL(url);
        } else {
            this.redraw();
        }
    }

    updateEntryById(entryId: string) {
        const {merchant, store, useAliasInURL} = this.options;
        if (!store) {
            console.error("Store is required to set entry to id", entryId);
            return
        }
        if (useAliasInURL) {
            const editingSettingsVersion = getEditingOrLiveSettingsVersion(merchant);
            this.selectedEntry = store.findBy({alias: entryId, settingsVersion: editingSettingsVersion}) || store.get(entryId);
        } else {
            this.selectedEntry = store.get(entryId);
        }
    }

    setOptions(options: this["options"]) {
        super.setOptions(options);
        const {args} = options;
        if (args) {
            if (args.length > 0) {
                this.updateEntryById(args[0]);
            } else {
                this.goToEntry(null);
            }
        }
    }

    getEntryRowClass() {
        return MakeLinkingRowClass((entry: Entry, event: MouseEvent) => {
            const specialKeyPressed = event.shiftKey || event.ctrlKey || event.metaKey;
            if (specialKeyPressed) {
                openWindowWithOpener(this.getUrlForEntry(entry));
            } else {
                this.goToEntry(entry);
            }
        });
    }

    makeTable(entries: Entry[], columns: ColumnLike<Entry>[], extraOptions: Record<string, any> = {}) {
        return <SimpleTable columns={columns} entries={entries} rowClass={this.getEntryRowClass()} {...extraOptions} />
    }
}
